import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import {
  CreateRecurringSpaBookingPayload,
  CreateSpaBookingPayload,
  CreateSpaTipsPayload,
  NoShowSpaBookingPayload,
  RescheduleSpaBookingPayload,
  SpaAvailabilityParams,
  SpaAvailabilitySlot,
  SpaBooking,
  SpaBookingCompleteResult,
  SpaBookingListParams,
  SpaBookingMembership,
  SpaBookingMembershipParams,
  SpaBookingTip,
  SpaCancelSeriesResult,
  SpaCategory,
  SpaCategoryPayload,
  SpaConsumableAdjustPayload,
  SpaConsumableItem,
  SpaConsumableItemPayload,
  SpaConsumableMovement,
  SpaConsumableMovementPage,
  SpaConsumableMovementParams,
  SpaConsumableReceivePayload,
  SpaConsumableStockCountPayload,
  SpaConsumableUsageParams,
  SpaConsumableUsageResult,
  SpaConsumableUsageRow,
  SpaConsumableVarianceRow,
  SpaIntakeAnswers,
  SpaIntakeForm,
  SpaIntakeFormPayload,
  SpaRecurringBookingResult,
  SpaRecurringOccurrence,
  SpaReportSummary,
  SpaReportSummaryParams,
  SpaRoom,
  SpaServiceItem,
  SpaServicePayload,
  SpaShift,
  SpaShiftListParams,
  SpaShiftPayload,
  SpaTherapist,
  SpaTipsResult,
} from './model'

/** Endpoints tolerate `[...]`, `{ data: [...] }` and `{ items: [...] }` envelopes. */
const unwrapList = <T>(body: T[] | { data?: T[]; items?: T[] } | undefined | null): T[] => {
  const list = Array.isArray(body) ? body : (body?.data ?? body?.items)
  return Array.isArray(list) ? list : []
}

/**
 * Pull an array out of a response body that may be bare, or wrapped under any of the envelope keys
 * this API uses across modules. Written once here rather than guessed per call site: the
 * consumables endpoints are being built in parallel with this client, and a page that blanks
 * because the array arrived under `items` instead of `data` is a bug the user sees, not the BE.
 */
const pickArray = (body: unknown, keys: readonly string[]): Record<string, unknown>[] => {
  if (Array.isArray(body)) return body as Record<string, unknown>[]
  if (!body || typeof body !== 'object') return []
  const record = body as Record<string, unknown>
  for (const key of keys) {
    if (Array.isArray(record[key])) return record[key] as Record<string, unknown>[]
  }
  return []
}

/** Keys `POST /spa/bookings/:id/complete` may echo its deduction list under. */
const CONSUMABLE_ECHO_KEYS = ['consumables', 'deducted', 'movements', 'consumableUses'] as const

/** Keys `GET /spa/consumables/usage` may carry its stock-count variances under. */
const VARIANCE_KEYS = ['variance', 'variances'] as const

/** Keys the tip endpoints may carry the booking's tip list under. */
const TIP_KEYS = ['tips', 'data', 'items'] as const

/** Keys `POST /spa/bookings/recurring/preview` may carry its per-occurrence rows under. */
const RECURRING_OCCURRENCE_KEYS = ['occurrences', 'data', 'items'] as const

/**
 * Both tip endpoints answer with the booking's WHOLE list plus its total, so they are parsed once
 * here rather than twice at the call sites.
 *
 * The total is recomputed only when the BE did not send one: the rows are the same money, and a
 * payment summary reading "฿0" over a list that plainly is not empty is worse than a sum the
 * client did itself.
 */
const toTipsResult = (body: unknown): SpaTipsResult => {
  const record = (body ?? {}) as Record<string, unknown>
  const tips = pickArray(record, TIP_KEYS).map((row) => deserialize(row, SpaBookingTip))
  const reported = Number(record.tipTotal ?? NaN)
  return SpaTipsResult.from({
    tips,
    tipTotal: Number.isFinite(reported)
      ? reported
      : tips.reduce((sum, tip) => sum + (Number(tip.amount) || 0), 0),
  })
}

/**
 * `{ intake: {...} | null }` → a form, or null when the guest has not filled one in.
 *
 * `answers` is opaque JSON to the BE, which is why it is the one field worth probing for: a
 * backend that returns the answer map flat (rather than wrapped in its stored row) must still
 * read as a filled-in form instead of an empty one.
 */
const toIntakeForm = (body: unknown): SpaIntakeForm | null => {
  const record = (body ?? {}) as Record<string, unknown>
  const intake = (record.intake ?? record.data ?? null) as Record<string, unknown> | null
  if (!intake || typeof intake !== 'object') return null
  const nested = intake.answers
  const answers = (nested && typeof nested === 'object'
    ? nested
    : intake) as unknown as SpaIntakeAnswers
  return SpaIntakeForm.from({
    answers,
    signedName: (intake.signedName ?? null) as string | null,
  })
}

/**
 * Wellness & Spa v1 client — mirrors the frozen contract (design §3).
 *
 * NOTE: there are deliberately NO payment methods here. A spa booking IS a facility booking, so
 * marking it paid / verifying its slip goes through the existing
 * `client.facilities.markFacilityBookingPaidCash` / `updateFacilityBookingSlip` with the same
 * booking id. Adding a parallel payment client would fork the receipt flow.
 */
export const SpaService = (client: AxiosInstance) => {
  return {
    /* ---------------------------------------------------------------------- */
    /* Categories                                                             */
    /* ---------------------------------------------------------------------- */
    async getSpaCategories(): Promise<SpaCategory[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[] | { data?: Record<string, unknown>[] }>(
          '/spa/categories'
        )
      )
      return unwrapList(data).map((item) => deserialize(item, SpaCategory))
    },
    async createSpaCategory(payload: SpaCategoryPayload): Promise<SpaCategory> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/spa/categories', payload)
      )
      return deserialize(data, SpaCategory)
    },
    async updateSpaCategory(id: string, payload: SpaCategoryPayload): Promise<SpaCategory> {
      const { data } = await wrapApiCall(() =>
        client.put<Record<string, unknown>>(`/spa/categories/${id}`, payload)
      )
      return deserialize(data, SpaCategory)
    },
    /** Soft delete — rejected by the BE when services are still attached. */
    async deleteSpaCategory(id: string): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ success?: boolean }>(`/spa/categories/${id}`)
      )
      return !!data?.success
    },

    /* ---------------------------------------------------------------------- */
    /* Services (treatments) + their duration options                          */
    /* ---------------------------------------------------------------------- */
    async getSpaServices(params?: {
      categoryId?: string
      active?: boolean
    }): Promise<SpaServiceItem[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[] | { data?: Record<string, unknown>[] }>(
          '/spa/services',
          { params }
        )
      )
      return unwrapList(data).map((item) => deserialize(item, SpaServiceItem))
    },
    async createSpaService(payload: SpaServicePayload): Promise<SpaServiceItem> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/spa/services', payload)
      )
      return deserialize(data, SpaServiceItem)
    },
    /** Replaces the options / rooms / therapists sets wholesale (options upsert by id). */
    async updateSpaService(id: string, payload: SpaServicePayload): Promise<SpaServiceItem> {
      const { data } = await wrapApiCall(() =>
        client.put<Record<string, unknown>>(`/spa/services/${id}`, payload)
      )
      return deserialize(data, SpaServiceItem)
    },
    async deleteSpaService(id: string): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ success?: boolean }>(`/spa/services/${id}`)
      )
      return !!data?.success
    },

    /* ---------------------------------------------------------------------- */
    /* Rooms + therapists                                                      */
    /* ---------------------------------------------------------------------- */
    /**
     * The branch's wellness rooms, for the treatment-room picker. Read-only on purpose: a
     * treatment room is a `rooms` row, so it is created and edited on the Rooms settings page
     * (where its capacity is the bed count) rather than duplicated under spa settings.
     */
    async getSpaRooms(): Promise<SpaRoom[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[] | { data?: Record<string, unknown>[] }>('/spa/rooms')
      )
      return unwrapList(data).map((item) => deserialize(item, SpaRoom))
    },
    /** Staff mapped to at least one treatment. The settings skill-matrix picker uses the staff API. */
    async getSpaTherapists(): Promise<SpaTherapist[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[] | { data?: Record<string, unknown>[] }>(
          '/spa/therapists'
        )
      )
      return unwrapList(data).map((item) => deserialize(item, SpaTherapist))
    },

    /* ---------------------------------------------------------------------- */
    /* Availability                                                           */
    /* ---------------------------------------------------------------------- */
    /**
     * 15-minute start grid for one treatment on one day. A slot is returned only when a mapped
     * room is free for the whole span INCLUDING buffer and (when required) enough mapped
     * therapists are free too — so the room/therapist pickers can just filter to the slot.
     */
    async getSpaAvailability(params: SpaAvailabilityParams): Promise<SpaAvailabilitySlot[]> {
      const { data } = await wrapApiCall(() =>
        client.get<{ slots?: Record<string, unknown>[] }>('/spa/availability', { params })
      )
      const slots = Array.isArray(data?.slots) ? data.slots : []
      return slots.map((item) => deserialize(item, SpaAvailabilitySlot))
    },

    /* ---------------------------------------------------------------------- */
    /* Bookings                                                               */
    /* ---------------------------------------------------------------------- */
    async getSpaBookings(params: SpaBookingListParams): Promise<SpaBooking[]> {
      const { data } = await wrapApiCall(() =>
        client.get<{ bookings?: Record<string, unknown>[] }>('/spa/bookings', { params })
      )
      const bookings = Array.isArray(data?.bookings) ? data.bookings : []
      return bookings.map((item) => deserialize(item, SpaBooking))
    },
    async createSpaBooking(payload: CreateSpaBookingPayload): Promise<SpaBooking> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/spa/bookings', payload)
      )
      return deserialize(data, SpaBooking)
    },
    /**
     * จองประจำ dry run: one row per occurrence with its own availability and, when a week cannot
     * be booked, the reason. No booking is created. Same body as the write, so what staff review
     * is exactly what they are about to book.
     *
     * The array is unwrapped defensively (`{ occurrences }` is the shipped shape) for the same
     * reason every other list in this client is — a preview that silently renders empty reads as
     * "no weeks match" rather than as an envelope mismatch.
     */
    async previewRecurringSpaBooking(
      payload: CreateRecurringSpaBookingPayload
    ): Promise<SpaRecurringOccurrence[]> {
      const { data } = await wrapApiCall(() =>
        client.post<{ occurrences?: SpaRecurringOccurrence[] }>(
          '/spa/bookings/recurring/preview',
          payload
        )
      )
      return pickArray(data, RECURRING_OCCURRENCE_KEYS) as unknown as SpaRecurringOccurrence[]
    },
    /**
     * Book the whole series under one `recurringGroupId`. PAID only — the BE rejects a
     * PACKAGE/CREDIT series outright, which is why the modal hides those options while จองประจำ
     * is on.
     */
    async createRecurringSpaBooking(
      payload: CreateRecurringSpaBookingPayload
    ): Promise<SpaRecurringBookingResult> {
      const { data } = await wrapApiCall(() =>
        client.post<SpaRecurringBookingResult>('/spa/bookings/recurring', payload)
      )
      return {
        recurringGroupId: data?.recurringGroupId ?? '',
        created: Array.isArray(data?.created) ? data.created : [],
        skipped: Array.isArray(data?.skipped) ? data.skipped : [],
      }
    },
    /**
     * Cancel every REMAINING occurrence of a series (future, not yet started, not completed).
     * Occurrences that already happened are reported as `skipped` rather than rewritten.
     */
    async cancelSpaBookingSeries(
      recurringGroupId: string,
      reason?: string
    ): Promise<SpaCancelSeriesResult> {
      const { data } = await wrapApiCall(() =>
        client.delete<SpaCancelSeriesResult>(`/spa/bookings/recurring/${recurringGroupId}`, {
          ...(reason ? { params: { reason } } : {}),
        })
      )
      return {
        recurringGroupId: data?.recurringGroupId ?? recurringGroupId,
        cancelled: Number(data?.cancelled ?? 0),
        skipped: Number(data?.skipped ?? 0),
      }
    },
    /**
     * Cancel — the BE applies the treatment's own cancel window
     * (`SpaService.bookingCancelTimeMinutes`) and persists `reason` on the booking.
     */
    async cancelSpaBooking(id: string, reason?: string): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.post<{ success?: boolean }>(`/spa/bookings/${id}/cancel`, reason ? { reason } : {})
      )
      return !!data?.success
    },
    /**
     * Move a booking to a new time (and optionally room / therapists). Service, duration, price
     * and payment are untouched — a reschedule is not a re-price. The BE re-runs the whole
     * conflict pipeline, so a 4xx here means the new slot is genuinely unavailable.
     */
    async rescheduleSpaBooking(
      id: string,
      payload: RescheduleSpaBookingPayload
    ): Promise<SpaBooking> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>(`/spa/bookings/${id}/reschedule`, payload)
      )
      return deserialize(data, SpaBooking)
    },
    /**
     * The guest has arrived. Stamps `checkInAt`; the BE 409s when the booking is already checked
     * in, completed, cancelled or a no-show, so the button never needs to guess.
     */
    async checkInSpaBooking(id: string): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.post<{ success?: boolean }>(`/spa/bookings/${id}/check-in`, {})
      )
      return data?.success !== false
    },
    /**
     * The guest never arrived. Closes the booking as NO_SHOW and, when `forfeitDeposit` is set,
     * keeps the deposit in the same transaction — the two decisions are one action at the counter,
     * so they must not be two requests that can half-fail.
     *
     * Returns the updated board row, so the caller can refresh from the response rather than
     * guessing what the booking now looks like.
     */
    async noShowSpaBooking(id: string, payload?: NoShowSpaBookingPayload): Promise<SpaBooking> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>(`/spa/bookings/${id}/no-show`, payload ?? {})
      )
      return deserialize(data, SpaBooking)
    },
    /**
     * Close the treatment. In one BE transaction this stamps `completedAt` AND deducts the
     * resolved recipe (qtyPerGuest × guestCount) as SERVICE_USE movements — which is why the
     * response is worth reading rather than discarding: it echoes what actually left the shelf.
     *
     * Stock is never a blocker; a deduction that drives an item negative still completes. The
     * echo keys are probed because the BE ships in parallel with this client.
     */
    async completeSpaBooking(id: string): Promise<SpaBookingCompleteResult> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>(`/spa/bookings/${id}/complete`, {})
      )
      const body = data ?? {}
      // The deduction list may be echoed at the top level or nested under the booking it belongs
      // to; both spellings mean the same thing to the detail sheet.
      const nested = (body.booking ?? body.data) as Record<string, unknown> | undefined
      const topRows = pickArray(body, CONSUMABLE_ECHO_KEYS)
      // Normalise into one shape and let the model's own decorators do the parsing, so the
      // timestamp lands in the same Bangkok-local Dayjs every other spa field uses.
      return deserialize(
        {
          completedAt: body.completedAt ?? nested?.completedAt ?? null,
          consumables: topRows.length ? topRows : pickArray(nested, CONSUMABLE_ECHO_KEYS),
        },
        SpaBookingCompleteResult
      )
    },
    /**
     * Memberships whose quota covers this (treatment, duration) for this member — the spa twin of
     * `findFacilityBookingMembership`. Returns an empty list (not an error) when nothing covers
     * it, so the modal can simply hide the package option.
     */
    async getSpaBookingMemberships(
      params: SpaBookingMembershipParams
    ): Promise<SpaBookingMembership[]> {
      const { data } = await wrapApiCall(() =>
        client.get<{ memberships?: Record<string, unknown>[] }>('/spa/bookings/membership', {
          params,
        })
      )
      const memberships = Array.isArray(data?.memberships) ? data.memberships : []
      return memberships.map((item) => deserialize(item, SpaBookingMembership))
    },

    /* ---------------------------------------------------------------------- */
    /* Tips                                                                   */
    /* ---------------------------------------------------------------------- */
    /**
     * Record tips for a finished treatment. One request per counter transaction, however many
     * therapists it splits across — the BE appends a row per entry inside one call, so two
     * therapists cannot half-succeed.
     *
     * Accepted on a COMPLETED booking (that is the normal moment); only CANCELLED / NO_SHOW are
     * refused. Returns the booking's whole tip list, so the caller never re-reads the board.
     */
    async createSpaBookingTips(id: string, payload: CreateSpaTipsPayload): Promise<SpaTipsResult> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>(`/spa/bookings/${id}/tips`, payload)
      )
      return toTipsResult(data)
    },
    /** Undo one tip row — the staff-mistake path. Answers with what is left. */
    async deleteSpaTip(tipId: string): Promise<SpaTipsResult> {
      const { data } = await wrapApiCall(() =>
        client.delete<Record<string, unknown>>(`/spa/tips/${tipId}`)
      )
      return toTipsResult(data)
    },

    /* ---------------------------------------------------------------------- */
    /* Intake form                                                            */
    /* ---------------------------------------------------------------------- */
    /** The guest's pre-treatment questionnaire, or null when nobody has filled one in. */
    async getSpaBookingIntake(id: string): Promise<SpaIntakeForm | null> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/spa/bookings/${id}/intake`)
      )
      return toIntakeForm(data)
    },
    /**
     * Upsert the questionnaire. Editable until the treatment is completed — after that the BE
     * 409s, because the answers are what the therapist actually worked from and rewriting them
     * afterwards would rewrite the record of the session. Reading stays open forever.
     */
    async saveSpaBookingIntake(id: string, payload: SpaIntakeFormPayload): Promise<SpaIntakeForm> {
      const { data } = await wrapApiCall(() =>
        client.put<Record<string, unknown>>(`/spa/bookings/${id}/intake`, payload)
      )
      // A PUT that echoes nothing recognisable still succeeded — fall back to what was just sent
      // rather than telling the user their answers vanished.
      return (
        toIntakeForm(data) ??
        SpaIntakeForm.from({ answers: payload.answers, signedName: payload.signedName ?? null })
      )
    },

    /* ---------------------------------------------------------------------- */
    /* Therapist shifts                                                       */
    /* ---------------------------------------------------------------------- */
    /**
     * The roster for a date range. Times stay raw 'HH:mm' strings — a shift is a wall-clock fact
     * about the branch's own day, so parsing it into a timestamp would invite a timezone shift.
     */
    async getSpaShifts(params: SpaShiftListParams): Promise<SpaShift[]> {
      const { data } = await wrapApiCall(() =>
        client.get<{ shifts?: Record<string, unknown>[] }>('/spa/shifts', { params })
      )
      const shifts = Array.isArray(data?.shifts) ? data.shifts : []
      return shifts.map((item) => deserialize(item, SpaShift))
    },
    /** The BE rejects an overlap with the same staff member's other shift that day with a 400. */
    async createSpaShift(payload: SpaShiftPayload): Promise<SpaShift> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/spa/shifts', payload)
      )
      return deserialize(data, SpaShift)
    },
    async updateSpaShift(id: string, payload: SpaShiftPayload): Promise<SpaShift> {
      const { data } = await wrapApiCall(() =>
        client.put<Record<string, unknown>>(`/spa/shifts/${id}`, payload)
      )
      return deserialize(data, SpaShift)
    },
    /** Hard delete — a shift is a plan, not a ledger row, so there is nothing to keep. */
    async deleteSpaShift(id: string): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete<{ success?: boolean }>(`/spa/shifts/${id}`)
      )
      return data?.success !== false
    },

    /* ---------------------------------------------------------------------- */
    /* Reports                                                                */
    /* ---------------------------------------------------------------------- */
    /**
     * One aggregate for an inclusive date range, sliced by treatment, category, room, therapist,
     * customer source and day. Money is THB throughout (team convention), never satang.
     */
    async getSpaReportSummary(params: SpaReportSummaryParams): Promise<SpaReportSummary> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>('/spa/reports/summary', { params })
      )
      return deserialize(data, SpaReportSummary)
    },

    /* ---------------------------------------------------------------------- */
    /* Consumables — catalog, stock movements and usage                        */
    /* ---------------------------------------------------------------------- */
    async getSpaConsumables(): Promise<SpaConsumableItem[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[] | { data?: Record<string, unknown>[] }>(
          '/spa/consumables'
        )
      )
      return unwrapList(data).map((item) => deserialize(item, SpaConsumableItem))
    },
    async createSpaConsumable(payload: SpaConsumableItemPayload): Promise<SpaConsumableItem> {
      const { data } = await wrapApiCall(() =>
        client.post<Record<string, unknown>>('/spa/consumables', payload)
      )
      return deserialize(data, SpaConsumableItem)
    },
    /** `unit` is only accepted while the item has no movements — the BE 400s otherwise. */
    async updateSpaConsumable(
      id: string,
      payload: SpaConsumableItemPayload
    ): Promise<SpaConsumableItem> {
      const { data } = await wrapApiCall(() =>
        client.put<Record<string, unknown>>(`/spa/consumables/${id}`, payload)
      )
      return deserialize(data, SpaConsumableItem)
    },
    /** `qty` must already be in base units; the receive modal does the pack arithmetic. */
    async receiveSpaConsumable(id: string, payload: SpaConsumableReceivePayload): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.post<{ success?: boolean }>(`/spa/consumables/${id}/receive`, payload)
      )
      return data?.success !== false
    },
    async adjustSpaConsumable(id: string, payload: SpaConsumableAdjustPayload): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.post<{ success?: boolean }>(`/spa/consumables/${id}/adjust`, payload)
      )
      return data?.success !== false
    },
    /** Writes a STOCK_COUNT movement whose delta reconciles on-hand to the counted figure. */
    async stockCountSpaConsumable(
      id: string,
      payload: SpaConsumableStockCountPayload
    ): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.post<{ success?: boolean }>(`/spa/consumables/${id}/stock-count`, payload)
      )
      return data?.success !== false
    },
    /** One page of the ledger, newest first. */
    async getSpaConsumableMovements(
      id: string,
      params?: SpaConsumableMovementParams
    ): Promise<SpaConsumableMovementPage> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/spa/consumables/${id}/movements`, { params })
      )
      const body = data ?? {}
      const rows = pickArray(body, ['movements', 'items', 'data'])
      const items = rows.map((item) => deserialize(item, SpaConsumableMovement))
      const pagination = (body.pagination ?? {}) as Record<string, unknown>
      const rawTotal = Number(pagination.total ?? body.total ?? body.totalCount ?? NaN)
      return SpaConsumableMovementPage.from({
        items,
        // A BE that does not report a total still has to page: fall back to what arrived, which
        // keeps the pager honest for a single page and simply hides it beyond that.
        total: Number.isFinite(rawTotal) ? rawTotal : items.length,
      })
    },
    /**
     * Per-item COGS over a date range, from SERVICE_USE movements only — plus the STOCK_COUNT
     * variances recorded in the same range, when the BE ships them.
     *
     * `variance` is read straight off the body rather than through `pickArray`'s envelope probe:
     * it is a named sibling of the usage rows, so a bare-array response (the oldest shape this
     * client still accepts) simply has none, and the section stays hidden.
     */
    async getSpaConsumableUsage(
      params: SpaConsumableUsageParams
    ): Promise<SpaConsumableUsageResult> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[] | Record<string, unknown>>('/spa/consumables/usage', {
          params,
        })
      )
      const items = pickArray(data, ['items', 'data', 'usage']).map((item) =>
        deserialize(item, SpaConsumableUsageRow)
      )
      const variance = pickArray(Array.isArray(data) ? undefined : data, VARIANCE_KEYS).map(
        (item) => deserialize(item, SpaConsumableVarianceRow)
      )
      return SpaConsumableUsageResult.from({ items, variance })
    },
  }
}
